import type {
  BuyingExceptions,
  StoredSupplyNetworkDocument,
  SupplyNetworkGroup,
  ItemSourceRule,
  LocationOverrides,
  LocationRole,
  RestockRelease,
  RoleBehaviors,
  StockRelease,
  StockReleasePolicy,
  SupplyNetwork,
  SupplyNetworkDivision,
  SupplyNetworkDocument,
  SupplyNetworkLane,
  SupplyNetworkLocation,
} from "@erp/shared-types";
import {
  GROUP_COLORS,
  SUPPLY_NETWORK_DOCUMENT_VERSION,
} from "@erp/shared-types";

/**
 * The supply network's rules, shared by the Supply Network page (its previews)
 * and the backend (what it publishes for the replenishment engine), so the two
 * can never rank sources differently. Pure and I/O-free.
 */

export type SupplyNetworkIndex = {
  locationsById: Map<string, SupplyNetworkLocation>;
  rolesById: Map<string, LocationRole>;
  groupsById: Map<string, SupplyNetworkGroup>;
  /** What a location without a role follows. */
  defaultRole: LocationRole;
};

export function indexSupplyNetwork(network: SupplyNetwork): SupplyNetworkIndex {
  return {
    locationsById: new Map(network.locations.map((l) => [l.id, l])),
    rolesById: new Map(network.roles.map((r) => [r.id, r])),
    groupsById: new Map(network.groups.map((g) => [g.id, g])),
    defaultRole: network.defaultRole,
  };
}

/** What a location's master data says about it, keyed by location id. */
export type SupplyNetworkLocationFacts = Omit<
  SupplyNetworkLocation,
  "id" | "roleId" | "groupId" | "overrides"
>;

export type SupplyNetworkMasterData = {
  divisions: SupplyNetworkDivision[];
  locations: Map<string, SupplyNetworkLocationFacts>;
  /** Item names by SKU, for item rules. Unknown SKUs show the SKU. */
  itemNames: Map<string, string>;
};

/**
 * A stored document joined with master data. Every location in master data is
 * part of the network: one the document doesn't mention yet has no role, so it
 * follows the default role. Settings for a location master data no longer has
 * are dropped, and so is a location's group if the group is gone. An older
 * document is upgraded first.
 */
export function supplyNetworkFromDocument(
  stored: StoredSupplyNetworkDocument,
  master: SupplyNetworkMasterData,
): SupplyNetwork {
  const document = upgradeSupplyNetworkDocument(stored, master);
  const settingsById = new Map(document.locations.map((l) => [l.id, l]));
  const groupIds = new Set(document.groups.map((group) => group.id));
  const locations: SupplyNetworkLocation[] = [...master.locations].map(
    ([id, facts]) => {
      const settings = settingsById.get(id);
      const groupId = settings?.groupId ?? null;
      return {
        id,
        roleId: settings?.roleId ?? null,
        ...(groupId !== null && groupIds.has(groupId) ? { groupId } : {}),
        ...(settings?.overrides ? { overrides: settings.overrides } : {}),
        ...facts,
      };
    },
  );
  return {
    roles: document.roles,
    defaultRole: document.defaultRole,
    divisions: master.divisions,
    groups: document.groups,
    locations,
    lanes: document.lanes,
    siblingRule: document.siblingRule,
    laneDefaults: document.laneDefaults,
    ...(document.floorLevels ? { floorLevels: document.floorLevels } : {}),
    ...(document.transferRelease
      ? { transferRelease: document.transferRelease }
      : {}),
    itemRules: document.itemRules.map((rule) => ({
      ...rule,
      itemName: master.itemNames.get(rule.sku) ?? rule.sku,
    })),
  };
}

/** The part of a network a version stores. */
export function supplyNetworkToDocument(
  network: SupplyNetwork,
): SupplyNetworkDocument {
  return {
    schemaVersion: SUPPLY_NETWORK_DOCUMENT_VERSION,
    roles: network.roles,
    defaultRole: network.defaultRole,
    groups: network.groups,
    locations: network.locations
      .filter(
        (location) =>
          location.roleId !== null ||
          location.groupId != null ||
          Object.keys(location.overrides ?? {}).length > 0,
      )
      .map((location) => ({
        id: location.id,
        roleId: location.roleId,
        ...(location.groupId != null ? { groupId: location.groupId } : {}),
        ...(location.overrides && Object.keys(location.overrides).length > 0
          ? { overrides: location.overrides }
          : {}),
      })),
    lanes: network.lanes,
    siblingRule: network.siblingRule,
    laneDefaults: network.laneDefaults,
    ...(network.floorLevels ? { floorLevels: network.floorLevels } : {}),
    ...(network.transferRelease
      ? { transferRelease: network.transferRelease }
      : {}),
    itemRules: network.itemRules.map(
      ({
        id,
        sku,
        locationId,
        mode,
        sourceLocationId,
        vendorId,
        importedFromGains,
      }) => ({
        id,
        sku,
        locationId,
        mode,
        sourceLocationId,
        ...(vendorId ? { vendorId } : {}),
        ...(importedFromGains ? { importedFromGains } : {}),
      }),
    ),
  };
}

/** The group that holds locations whose role supplied every division (v1). */
export const ALL_LOCATIONS_GROUP_ID = "all-locations";

/** Where a location sits before it has groups: its division, or every one. */
export type DivisionPlacement = {
  id: string;
  divisionId: string | null;
  /** Supplies every division, like a distribution centre. */
  suppliesEveryDivision: boolean;
};

/**
 * One group per division its locations use, in master-data order, and each
 * location in its division's group. Locations that supply every division go
 * into a top-level "All locations" group above the division groups instead,
 * so their lanes never cross groups. The conversions and the version 1
 * upgrade start from this.
 */
export function groupsFromDivisions(
  divisions: readonly SupplyNetworkDivision[],
  locations: readonly DivisionPlacement[],
  siblingTransfersOf: (divisionId: string) => "on" | "off" | undefined = () =>
    undefined,
): { groups: SupplyNetworkGroup[]; groupIdOf: Map<string, string> } {
  const everywhere = locations.filter((l) => l.suppliesEveryDivision);
  const top: SupplyNetworkGroup | null =
    everywhere.length > 0
      ? {
          id: ALL_LOCATIONS_GROUP_ID,
          name: "All locations",
          parentId: null,
          color: "slate",
        }
      : null;
  const used = new Set(
    locations.filter((l) => !l.suppliesEveryDivision).map((l) => l.divisionId),
  );
  const groups = divisions
    .filter((division) => used.has(division.id))
    .map((division, position): SupplyNetworkGroup => {
      const siblingTransfers = siblingTransfersOf(division.id);
      return {
        id: division.id,
        name: division.name,
        parentId: top?.id ?? null,
        color: GROUP_COLORS[position % GROUP_COLORS.length] ?? "slate",
        ...(siblingTransfers ? { siblingTransfers } : {}),
      };
    });
  const groupIds = new Set(groups.map((group) => group.id));
  const groupIdOf = new Map<string, string>();
  for (const location of locations) {
    if (location.suppliesEveryDivision) {
      groupIdOf.set(location.id, ALL_LOCATIONS_GROUP_ID);
    } else if (
      location.divisionId !== null &&
      groupIds.has(location.divisionId)
    ) {
      groupIdOf.set(location.id, location.divisionId);
    }
  }
  return { groups: top ? [top, ...groups] : groups, groupIdOf };
}

/**
 * A stored document in the current version. A version 1 document, from before
 * groups, gets its groups from {@link groupsFromDivisions}, with each
 * division's sibling setting moved onto its group.
 */
export function upgradeSupplyNetworkDocument(
  stored: StoredSupplyNetworkDocument,
  master: Pick<SupplyNetworkMasterData, "divisions" | "locations">,
): SupplyNetworkDocument {
  if (stored.schemaVersion === SUPPLY_NETWORK_DOCUMENT_VERSION) return stored;
  const {
    divisionSettings,
    siblingRule: { sameDivisionOnly, ...siblingRule },
    roles,
    defaultRole,
    locations: settings,
    schemaVersion: _version1,
    ...rest
  } = stored;
  const suppliesEvery = new Set(
    [...roles, defaultRole]
      .filter((role) => role.suppliesEveryDivision)
      .map((role) => role.id),
  );
  const settingsById = new Map(settings.map((l) => [l.id, l]));
  const { groups, groupIdOf } = groupsFromDivisions(
    master.divisions,
    [...master.locations].map(([id, facts]) => ({
      id,
      divisionId: facts.divisionId,
      suppliesEveryDivision: suppliesEvery.has(
        settingsById.get(id)?.roleId ?? defaultRole.id,
      ),
    })),
    (divisionId) => divisionSettings[divisionId]?.siblingTransfers,
  );
  const strip = ({
    suppliesEveryDivision: _flag,
    ...role
  }: (typeof roles)[number]): LocationRole => role;
  return {
    ...rest,
    schemaVersion: SUPPLY_NETWORK_DOCUMENT_VERSION,
    roles: roles.map(strip),
    defaultRole: strip(defaultRole),
    groups,
    locations: [...master.locations].flatMap(([id]) => {
      const own = settingsById.get(id);
      const groupId = groupIdOf.get(id);
      if (own === undefined && groupId === undefined) return [];
      return [
        { ...(own ?? { id, roleId: null }), ...(groupId ? { groupId } : {}) },
      ];
    }),
    siblingRule: { ...siblingRule, sameGroupOnly: sameDivisionOnly },
  };
}

/** A group and its ancestors, nearest first; stops at a cycle. */
export function groupChain(
  index: SupplyNetworkIndex,
  groupId: string | null | undefined,
): SupplyNetworkGroup[] {
  const chain: SupplyNetworkGroup[] = [];
  const seen = new Set<string>();
  let current = groupId ? index.groupsById.get(groupId) : undefined;
  while (current !== undefined && !seen.has(current.id)) {
    chain.push(current);
    seen.add(current.id);
    current = current.parentId
      ? index.groupsById.get(current.parentId)
      : undefined;
  }
  return chain;
}

/** A location's group, or undefined while it is in none. */
export function groupOf(
  index: SupplyNetworkIndex,
  locationId: string,
): SupplyNetworkGroup | undefined {
  const groupId = index.locationsById.get(locationId)?.groupId;
  return groupId ? index.groupsById.get(groupId) : undefined;
}

/** Problems with a group tree: unknown parents and cycles, in plain words. */
export function groupTreeProblems(
  groups: readonly SupplyNetworkGroup[],
): string[] {
  const byId = new Map(groups.map((group) => [group.id, group]));
  const problems: string[] = [];
  for (const group of groups) {
    if (group.parentId !== null && !byId.has(group.parentId)) {
      problems.push(`Group ${group.name} has a parent that doesn't exist.`);
      continue;
    }
    const seen = new Set<string>([group.id]);
    let parentId = group.parentId;
    while (parentId !== null) {
      if (parentId === group.id) {
        problems.push(`Group ${group.name} is inside itself.`);
        break;
      }
      // A chain that runs into another loop: that loop's groups report it.
      if (seen.has(parentId)) break;
      seen.add(parentId);
      parentId = byId.get(parentId)?.parentId ?? null;
    }
  }
  return problems;
}

export function roleOf(
  index: SupplyNetworkIndex,
  locationId: string,
): LocationRole | undefined {
  const roleId = index.locationsById.get(locationId)?.roleId;
  return roleId ? index.rolesById.get(roleId) : undefined;
}

/**
 * The behaviors a location actually follows: its role's, or the system-wide
 * defaults while it has no role.
 */
export function behaviorsOf(
  index: SupplyNetworkIndex,
  locationId: string,
): LocationRole {
  const base = roleOf(index, locationId) ?? index.defaultRole;
  return applyOverrides(base, index.locationsById.get(locationId)?.overrides);
}

/**
 * A role's behaviors with a location's pinned settings on top. A pinned
 * waiting-customer floor never ends up stricter than the restocking one: if a
 * role change would make it so, it loosens to match.
 */
export function applyOverrides<R extends RoleBehaviors>(
  base: R,
  overrides: LocationOverrides | undefined,
): R {
  if (!overrides || Object.keys(overrides).length === 0) return base;
  const restock = overrides.restock ?? base.releasesStock.restock;
  const waiting =
    overrides.customerWaiting ?? base.releasesStock.customerWaiting;
  const doesNotBuy = overrides.doesNotBuy ?? base.doesNotBuy;
  return {
    ...base,
    buysFromVendors: overrides.buysFromVendors ?? base.buysFromVendors,
    ...(doesNotBuy ? { doesNotBuy } : {}),
    inboundTransfers: overrides.inboundTransfers ?? base.inboundTransfers,
    autoReleaseLineCap: autoReleaseLineCapOf(base, overrides),
    releasesStock: {
      restock,
      customerWaiting: isMoreProtective(waiting, restock) ? restock : waiting,
    },
  };
}

/** The daily auto-release line cap: a pinned value (null pins "no cap"), else the role's. */
function autoReleaseLineCapOf(
  base: RoleBehaviors,
  overrides: LocationOverrides,
): number | undefined {
  if (overrides.autoReleaseLineCap === undefined) {
    return base.autoReleaseLineCap;
  }
  return overrides.autoReleaseLineCap ?? undefined;
}

const OVERRIDE_KEYS: (keyof LocationOverrides)[] = [
  "buysFromVendors",
  "doesNotBuy",
  "restock",
  "customerWaiting",
  "inboundTransfers",
  "autoReleaseLineCap",
];

/** The behaviors a location overrides, for "Overridden" markers. */
export function overriddenBehaviors(
  index: SupplyNetworkIndex,
  locationId: string,
): Set<keyof LocationOverrides> {
  const overrides = index.locationsById.get(locationId)?.overrides ?? {};
  return new Set(OVERRIDE_KEYS.filter((key) => overrides[key] !== undefined));
}

export function canBuy(index: SupplyNetworkIndex, locationId: string): boolean {
  return behaviorsOf(index, locationId).buysFromVendors;
}

/** The suppliers and departments a location doesn't buy, when it buys at all. */
export function buyingExceptionsOf(
  index: SupplyNetworkIndex,
  locationId: string,
): BuyingExceptions | null {
  const behaviors = behaviorsOf(index, locationId);
  const exceptions = behaviors.doesNotBuy;
  if (!behaviors.buysFromVendors || !exceptions) return null;
  return exceptions.supplierIds.length + exceptions.itemGroupIds.length > 0
    ? exceptions
    : null;
}

/**
 * Whether exceptions keep a location from buying an item: the item is in a
 * department it doesn't buy, or every supplier that sells it is one it
 * doesn't buy. An item another supplier also sells is still bought, from
 * that supplier.
 */
export function exceptsItem(
  exceptions: BuyingExceptions | null,
  item: LaneItemFacts,
): boolean {
  if (exceptions === null) return false;
  if (exceptions.itemGroupIds.some((id) => item.itemGroupIds.has(id))) {
    return true;
  }
  const suppliers = [...item.supplierIds];
  return (
    suppliers.length > 0 &&
    suppliers.every((id) => exceptions.supplierIds.includes(id))
  );
}

/** Whether a location buys an item, or, without one, buys at all. */
export function buysItem(
  index: SupplyNetworkIndex,
  locationId: string,
  item?: LaneItemFacts,
): boolean {
  if (!canBuy(index, locationId)) return false;
  return (
    item === undefined ||
    !exceptsItem(buyingExceptionsOf(index, locationId), item)
  );
}

const RELEASE_ORDER: readonly StockRelease[] = [
  "above_max",
  "above_min",
  "all_available",
];

/** True when `release` keeps back more stock than `than`. */
export function isMoreProtective(
  release: StockRelease,
  than: StockRelease,
): boolean {
  return RELEASE_ORDER.indexOf(release) < RELEASE_ORDER.indexOf(than);
}

/**
 * Change the restocking floor, loosening the waiting-customer floor with it,
 * so a waiting customer never gets less than a routine top-up would.
 */
export function withRestockRelease(
  policy: StockReleasePolicy,
  restock: RestockRelease,
): StockReleasePolicy {
  return {
    restock,
    customerWaiting: isMoreProtective(policy.customerWaiting, restock)
      ? restock
      : policy.customerWaiting,
  };
}

/**
 * A lane between two groups: its source is outside the destination's group
 * and that group's ancestors. A distribution centre in a group above the
 * groups it supplies never crosses. "No group" counts as a group of its own.
 */
export function crossesGroup(
  lane: SupplyNetworkLane,
  index: SupplyNetworkIndex,
): boolean {
  const from = index.locationsById.get(lane.fromId);
  const to = index.locationsById.get(lane.toId);
  if (from === undefined || to === undefined) return false;
  const fromGroup = from.groupId ?? null;
  if (fromGroup === null) return (to.groupId ?? null) !== null;
  return !groupChain(index, to.groupId).some((g) => g.id === fromGroup);
}

/** Orders location ids by name, then id, so lists are stable. */
export function compareLocationsByName(index: SupplyNetworkIndex) {
  return function byName(a: string, b: string): number {
    const an = index.locationsById.get(a)?.name ?? a;
    const bn = index.locationsById.get(b)?.name ?? b;
    return an.localeCompare(bn) || a.localeCompare(b);
  };
}

/** Other locations fed by any of this location's upstreams. */
export function siblingsOf(
  lanes: SupplyNetworkLane[],
  index: SupplyNetworkIndex,
  locationId: string,
): string[] {
  const upstreams = new Set(
    lanes.filter((lane) => lane.toId === locationId).map((lane) => lane.fromId),
  );
  const siblings = new Set<string>();
  for (const lane of lanes) {
    if (upstreams.has(lane.fromId) && lane.toId !== locationId) {
      siblings.add(lane.toId);
    }
  }
  return [...siblings].sort(compareLocationsByName(index));
}

export type SiblingSetting =
  | {
      enabled: boolean;
      /** A group's override: the location's own group or an ancestor's. */
      source: "group";
      groupId: string;
    }
  | { enabled: boolean; source: "default" };

/** Whether a location takes part in sibling transfers, and why. */
export function siblingSettingOf(
  network: SupplyNetwork,
  index: SupplyNetworkIndex,
  locationId: string,
): SiblingSetting {
  const group = groupChain(
    index,
    index.locationsById.get(locationId)?.groupId,
  ).find((g) => g.siblingTransfers !== undefined);
  return group?.siblingTransfers
    ? {
        enabled: group.siblingTransfers === "on",
        source: "group",
        groupId: group.id,
      }
    : { enabled: network.siblingRule.enabled, source: "default" };
}

/**
 * The siblings the sibling rule lets this location pull from. Both ends must
 * take part: a group switched off neither gives to nor takes from siblings.
 * "Same group only" means the location's own group, not its subgroups.
 */
export function siblingSourcesOf(
  network: SupplyNetwork,
  index: SupplyNetworkIndex,
  lanes: SupplyNetworkLane[],
  locationId: string,
): string[] {
  const takesPart = (id: string) =>
    siblingSettingOf(network, index, id).enabled;
  if (!takesPart(locationId)) return [];
  const groupId = index.locationsById.get(locationId)?.groupId ?? null;
  return siblingsOf(lanes, index, locationId).filter(
    (id) =>
      takesPart(id) &&
      (!network.siblingRule.sameGroupOnly ||
        (index.locationsById.get(id)?.groupId ?? null) === groupId),
  );
}

export type AutomaticLaneSet = {
  /** The upstream these locations share. */
  upstreamId: string;
  memberIds: string[];
  /** The group the set is limited to when "same group only" is on. */
  groupId: string | null;
  sameGroupOnly: boolean;
};

/**
 * The sibling rule's lanes, summarized: one set per shared upstream (split by
 * group when "same group only" is on). Every member can transfer to every
 * other member of its set.
 */
export function automaticLaneSets(
  network: SupplyNetwork,
  index: SupplyNetworkIndex,
  lanes: SupplyNetworkLane[],
): AutomaticLaneSet[] {
  const rule = network.siblingRule;
  const byName = compareLocationsByName(index);
  const childrenOf = new Map<string, string[]>();
  for (const lane of lanes) {
    childrenOf.set(lane.fromId, [
      ...(childrenOf.get(lane.fromId) ?? []),
      lane.toId,
    ]);
  }
  const sets: AutomaticLaneSet[] = [];
  for (const [upstreamId, children] of childrenOf) {
    const buckets = new Map<string | null, string[]>();
    for (const id of children) {
      if (!siblingSettingOf(network, index, id).enabled) continue;
      const key = rule.sameGroupOnly
        ? (index.locationsById.get(id)?.groupId ?? null)
        : null;
      buckets.set(key, [...(buckets.get(key) ?? []), id]);
    }
    for (const [groupId, memberIds] of buckets) {
      if (memberIds.length < 2) continue;
      sets.push({
        upstreamId,
        memberIds: [...memberIds].sort(byName),
        groupId,
        sameGroupOnly: rule.sameGroupOnly,
      });
    }
  }
  return sets.sort((a, b) => byName(a.upstreamId, b.upstreamId));
}

/** How many one-way lanes the sibling rule adds across the network. */
export function automaticLaneCount(
  network: SupplyNetwork,
  index: SupplyNetworkIndex,
  lanes: SupplyNetworkLane[],
): number {
  return network.locations.reduce(
    (count, location) =>
      count + siblingSourcesOf(network, index, lanes, location.id).length,
    0,
  );
}

/** Locations that share an upstream with a location in another group. */
export function locationsWithCrossGroupSiblings(
  network: SupplyNetwork,
  index: SupplyNetworkIndex,
): string[] {
  return network.locations
    .filter((location) =>
      siblingsOf(network.lanes, index, location.id).some(
        (id) =>
          (index.locationsById.get(id)?.groupId ?? null) !==
          (location.groupId ?? null),
      ),
    )
    .map((location) => location.id);
}

export type SourceOption =
  | { kind: "item_rule"; sourceId: string; itemRuleId: string }
  | {
      kind: "lane";
      sourceId: string;
      priority: number;
      /** Set when the lane carries only some items. */
      productFilter?: SupplyNetworkLane["productFilter"];
    }
  | { kind: "sibling_rule"; siblingIds: string[]; priority: number }
  | {
      kind: "vendor";
      available: boolean;
      preferred: boolean;
      /** Buying only happens because an item rule overrides the role. */
      overridesRole?: true;
      /** Without an item: what the location buys from vendors, except. */
      except?: BuyingExceptions;
    };

/** What lane filters need to know about one item at one lane. */
export type LaneItemFacts = {
  itemGroupIds: ReadonlySet<string>;
  /** Suppliers that sell the item at either end of the lane. */
  supplierIds: ReadonlySet<string>;
};

/** Whether a lane carries an item. A lane without a filter carries every item. */
export function laneCarries(
  lane: SupplyNetworkLane,
  item: LaneItemFacts,
): boolean {
  const filter = lane.productFilter;
  if (!filter) return true;
  const matches =
    filter.itemGroupIds.some((id) => item.itemGroupIds.has(id)) ||
    filter.supplierIds.some((id) => item.supplierIds.has(id));
  return filter.mode === "include" ? matches : !matches;
}

/**
 * Lanes into a location, preferred first. With an item, only the lanes that
 * carry it; without one, every lane.
 */
export function lanesInto(
  lanes: SupplyNetworkLane[],
  locationId: string,
  item?: LaneItemFacts,
): SupplyNetworkLane[] {
  return lanes
    .filter(
      (lane) =>
        lane.toId === locationId &&
        (item === undefined || laneCarries(lane, item)),
    )
    .sort((a, b) => a.priority - b.priority || a.id.localeCompare(b.id));
}

/**
 * The lanes a location that doesn't buy can restock from: its preferred lane
 * for an item. Without an item, every filtered lane ahead of the first lane
 * that carries everything, and that lane, since which one an item uses
 * depends on the item.
 */
function lanesUsedWithoutBuying(
  incoming: SupplyNetworkLane[],
  item: LaneItemFacts | undefined,
): SupplyNetworkLane[] {
  if (item !== undefined) return incoming.slice(0, 1);
  const firstUnfiltered = incoming.findIndex((lane) => !lane.productFilter);
  return firstUnfiltered === -1
    ? incoming
    : incoming.slice(0, firstUnfiltered + 1);
}

/**
 * Where a location's stock comes from, most preferred first: an item rule,
 * then stored lanes by priority, then the sibling rule, then a vendor buy when
 * the location buys (or the item rule prefers buying).
 *
 * A location that doesn't buy always restocks from its preferred lane, even
 * when that source is short (the source then replenishes itself), so its
 * other lanes and siblings are never used. Without a lane it gets no stock.
 */
export function sourcesFor(
  network: SupplyNetwork,
  index: SupplyNetworkIndex,
  lanes: SupplyNetworkLane[],
  locationId: string,
  itemRule: ItemSourceRule | null,
  /** The item, when ranking for one: lanes that don't carry it are left out. */
  item?: LaneItemFacts,
): SourceOption[] {
  if (!index.locationsById.has(locationId)) return [];

  const roleBuys = buysItem(index, locationId, item);
  const exceptions =
    item === undefined ? buyingExceptionsOf(index, locationId) : null;
  const incoming = lanesInto(lanes, locationId, item);
  const options: SourceOption[] = (
    roleBuys ? incoming : lanesUsedWithoutBuying(incoming, item)
  ).map((lane) => ({
    kind: "lane",
    sourceId: lane.fromId,
    priority: lane.priority,
    ...(lane.productFilter && item === undefined
      ? { productFilter: lane.productFilter }
      : {}),
  }));

  const siblingIds = roleBuys
    ? siblingSourcesOf(network, index, lanes, locationId)
    : [];
  if (siblingIds.length > 0) {
    options.push({
      kind: "sibling_rule",
      siblingIds,
      priority: network.siblingRule.priority,
    });
  }

  // Item rules always win: preferring to buy makes the vendor available even
  // where the location's behaviors say it doesn't buy.
  const preferred = itemRule?.mode === "prefer_buy";
  const vendor: SourceOption = {
    kind: "vendor",
    available: roleBuys || preferred,
    preferred,
    ...(preferred && !roleBuys ? { overridesRole: true as const } : {}),
    ...(exceptions ? { except: exceptions } : {}),
  };
  if (preferred) options.unshift(vendor);
  else options.push(vendor);

  if (itemRule?.mode === "pin_source" && itemRule.sourceLocationId) {
    options.unshift({
      kind: "item_rule",
      sourceId: itemRule.sourceLocationId,
      itemRuleId: itemRule.id,
    });
  }
  return options;
}

export type ItemRuleOverride = "buys_despite_role" | "pins_without_lane";

/**
 * What an item rule makes happen that the network alone would not. Item rules
 * always win; these are notes, not errors.
 */
export function itemRuleOverrides(
  network: SupplyNetwork,
  index: SupplyNetworkIndex,
  lanes: SupplyNetworkLane[],
  rule: ItemSourceRule,
): ItemRuleOverride[] {
  const overrides: ItemRuleOverride[] = [];
  if (rule.mode === "prefer_buy" && !canBuy(index, rule.locationId)) {
    overrides.push("buys_despite_role");
  }
  const source = rule.sourceLocationId;
  if (rule.mode === "pin_source" && source) {
    const hasLane =
      lanes.some(
        (lane) => lane.fromId === source && lane.toId === rule.locationId,
      ) ||
      siblingSourcesOf(network, index, lanes, rule.locationId).includes(source);
    if (!hasLane) overrides.push("pins_without_lane");
  }
  return overrides;
}

/**
 * A location that cannot get stock: it doesn't buy and no lane reaches it, or
 * it buys but not some suppliers or departments, and no lane reaches it for
 * those. A location with no lanes that buys everything for itself is a valid
 * standalone setup.
 */
export type NetworkIssue = {
  kind: "no_source" | "excepted_without_lane";
  locationId: string;
};

export function networkIssues(
  network: SupplyNetwork,
  index: SupplyNetworkIndex,
  lanes: SupplyNetworkLane[],
): NetworkIssue[] {
  const hasIncoming = new Set(lanes.map((lane) => lane.toId));
  return network.locations.flatMap((location): NetworkIssue[] => {
    if (hasIncoming.has(location.id)) return [];
    if (!canBuy(index, location.id)) {
      return [{ kind: "no_source", locationId: location.id }];
    }
    return buyingExceptionsOf(index, location.id) === null
      ? []
      : [{ kind: "excepted_without_lane", locationId: location.id }];
  });
}
